import React, { useState, useEffect, useCallback } from 'react';
import { Reservation } from '../../types';
import Modal from '../shared/Modal';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency, formatStatus, getPaymentStatusColor } from '../../utils/formatters';
import { fetchPaymentsForReservation, PaymentRecord } from '../../services/paymentService';
import {
  PaymentAction,
  PaymentStatusActionButtons,
  PaymentStatusConfirmModal,
  statusUpdateResultText,
} from './PaymentStatusUpdate';
import { authService } from '../../services/auth';

interface PaymentPopupProps {
  reservation: Reservation;
  onClose: () => void;
  /** Called after a payment status is changed, so parents can refresh their own payment data. */
  onPaymentsChanged?: () => void;
}

const PaymentPopup: React.FC<PaymentPopupProps> = ({ reservation, onClose, onPaymentsChanged }) => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ payment: PaymentRecord; action: PaymentAction } | null>(null);
  const [resultBanner, setResultBanner] = useState<{ text: string; noOp: boolean } | null>(null);

  const loadPayments = useCallback(() => {
    const storeId = authService.getStoreId();
    if (!storeId) {
      setError('Store not available');
      setLoading(false);
      return;
    }

    const fetches = [fetchPaymentsForReservation(reservation.id, storeId)];
    if (reservation.originalReservationId) {
      fetches.push(fetchPaymentsForReservation(reservation.originalReservationId, storeId));
    }
    Promise.all(fetches)
      .then((results) => {
        const merged = results.flat();
        const unique = Array.from(new Map(merged.map((p) => [p.id, p])).values());
        setPayments(unique);
        setError(null);
      })
      .catch((err) => {
        setError('Failed to load payment records');
        console.error('Payment fetch error:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [reservation.id, reservation.originalReservationId]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const paymentStatus = payments.length > 0 ? payments[0].status : 'pending';

  const footer = (
    <button className="btn btn-primary" onClick={onClose}>
      OK
    </button>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Payment Details"
      size="medium"
      footer={footer}
    >
      <div className="payment-popup">
        <div className="payment-header">
          <span className="payment-ref">Reservation: {reservation.confirmationCode}</span>
          <span
            className="payment-status-badge"
            style={{ backgroundColor: getPaymentStatusColor(paymentStatus) }}
          >
            {formatStatus(paymentStatus)}
          </span>
        </div>

        {resultBanner && (
          <div className={`payment-result-banner ${resultBanner.noOp ? 'payment-result-banner--noop' : 'payment-result-banner--success'}`}>
            {resultBanner.text}
          </div>
        )}

        <div className="payment-history">
          <h4>Payment History</h4>
          {loading ? (
            <p className="no-payments">Loading payment records...</p>
          ) : error ? (
            <p className="no-payments">{error}</p>
          ) : payments.length > 0 ? (
            <div className="payment-transactions">
              {payments.map((payment) => (
                <div key={payment.id} className="payment-transaction">
                  <div className="transaction-row">
                    <span className={`transaction-amount ${payment.status === 'completed' ? '' : 'transaction-amount--pending'}`}>
                      {formatCurrency(payment.amount)}
                    </span>
                    <span
                      className="transaction-status-badge"
                      style={{ backgroundColor: getPaymentStatusColor(payment.status) }}
                    >
                      {formatStatus(payment.status)}
                    </span>
                  </div>
                  <div className="transaction-details">
                    <div className="detail-row">
                      <span className="detail-label">Payment Method</span>
                      <span className="detail-value">
                        {payment.payment_method ? formatStatus(payment.payment_method) : '—'}
                        {payment.card_brand && payment.last_four_digits
                          ? ` (${payment.card_brand} ****${payment.last_four_digits})`
                          : ''}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Type</span>
                      <span className="detail-value">
                        {payment.payment_type ? formatStatus(payment.payment_type) : '—'}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Gateway</span>
                      <span className="detail-value">{payment.gateway_provider}</span>
                    </div>
                    {payment.transaction_id && (
                      <div className="detail-row">
                        <span className="detail-label">Transaction ID</span>
                        <span className="detail-value detail-value--mono">{payment.transaction_id}</span>
                      </div>
                    )}
                    {payment.initiated_at && (
                      <div className="detail-row">
                        <span className="detail-label">Initiated</span>
                        <span className="detail-value">{formatDate(payment.initiated_at)}</span>
                      </div>
                    )}
                    {payment.processed_at && (
                      <div className="detail-row">
                        <span className="detail-label">Processed</span>
                        <span className="detail-value">{formatDate(payment.processed_at)}</span>
                      </div>
                    )}
                    {payment.confirmed_at && (
                      <div className="detail-row">
                        <span className="detail-label">Confirmed</span>
                        <span className="detail-value">{formatDate(payment.confirmed_at)}</span>
                      </div>
                    )}
                    {payment.last_error_code && (
                      <div className="detail-row detail-row--error">
                        <span className="detail-label">Error</span>
                        <span className="detail-value">
                          {payment.last_error_code}
                          {payment.last_error_message ? `: ${payment.last_error_message}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="transaction-actions">
                    <PaymentStatusActionButtons
                      payment={payment}
                      reservationStatus={reservation.status}
                      onAction={(p, action) => setPendingAction({ payment: p, action })}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-payments">No payments recorded</p>
          )}
        </div>

      </div>

      {pendingAction && (
        <PaymentStatusConfirmModal
          payment={pendingAction.payment}
          action={pendingAction.action}
          onClose={() => setPendingAction(null)}
          onUpdated={(response) => {
            setPendingAction(null);
            setResultBanner({ text: statusUpdateResultText(response), noOp: response.no_op });
            if (!response.no_op) {
              loadPayments();
              onPaymentsChanged?.();
            }
          }}
        />
      )}

      <style>{`
        .payment-popup {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xl);
        }

        .payment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .payment-ref {
          font-weight: 500;
          color: var(--text-secondary);
        }

        .payment-status-badge {
          padding: var(--spacing-xs) var(--spacing-md);
          border-radius: var(--radius-sm);
          color: white;
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
        }

        .payment-history {
          background: var(--background);
          padding: var(--spacing-md);
          border-radius: var(--radius-sm);
        }

        .payment-history h4 {
          margin: 0 0 var(--spacing-md) 0;
          font-size: 0.9rem;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .payment-transactions {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-sm);
        }

        .payment-transaction {
          padding: var(--spacing-md);
          background: var(--surface);
          border-radius: var(--radius-sm);
        }

        .transaction-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-sm);
        }

        .transaction-amount {
          font-weight: 700;
          font-size: 1.05rem;
          color: var(--status-confirmed);
        }

        .transaction-amount--pending {
          color: var(--text-secondary);
        }

        .transaction-status-badge {
          padding: 2px var(--spacing-sm);
          border-radius: var(--radius-sm);
          color: white;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
        }

        .transaction-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .transaction-actions {
          margin-top: var(--spacing-sm);
        }

        .transaction-actions:empty {
          display: none;
        }

        .payment-result-banner {
          padding: var(--spacing-sm) var(--spacing-md);
          border-radius: var(--radius-sm);
          font-size: 0.85rem;
        }

        .payment-result-banner--success {
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #065f46;
        }

        .payment-result-banner--noop {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          color: #075985;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.83rem;
          padding: 2px 0;
        }

        .detail-label {
          color: var(--text-secondary);
        }

        .detail-value {
          color: var(--text-primary);
          text-align: right;
        }

        .detail-value--mono {
          font-family: monospace;
          font-size: 0.78rem;
        }

        .detail-row--error .detail-value {
          color: #ef4444;
        }

        .no-payments {
          margin: 0;
          color: var(--text-secondary);
          font-style: italic;
          font-size: 0.9rem;
        }

      `}</style>
    </Modal>
  );
};

export default PaymentPopup;
